/**
 * Snakes and Ladders Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService — never raw alert().
 *
 * Follows BattleshipErrorHandler pattern.
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum SnakesLaddersErrorType {
  INVALID_MOVE = 'INVALID_MOVE',
  NOT_YOUR_TURN = 'NOT_YOUR_TURN',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  AI_TIMEOUT = 'AI_TIMEOUT',
  MULTIPLAYER_VALIDATION = 'MULTIPLAYER_VALIDATION',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface SnakesLaddersError {
  type: SnakesLaddersErrorType;
  message: string;
  details?: any;
  timestamp: number;
  recoverable: boolean;
}

export class SnakesLaddersErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: SnakesLaddersError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: SnakesLaddersErrorType,
    message: string,
    details?: any,
    showNotification: boolean = true
  ): SnakesLaddersError {
    const error: SnakesLaddersError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'SnakesAndLadders', additionalData: details },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: SnakesLaddersErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case SnakesLaddersErrorType.INVALID_MOVE:
      case SnakesLaddersErrorType.NOT_YOUR_TURN:
      case SnakesLaddersErrorType.GAME_ENDED:
      case SnakesLaddersErrorType.GAME_LOGIC:
      case SnakesLaddersErrorType.AI_TIMEOUT:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case SnakesLaddersErrorType.CONNECTION_ERROR:
      case SnakesLaddersErrorType.MULTIPLAYER_VALIDATION:
        return { errorType: ErrorType.MULTIPLAYER, severity: ErrorSeverity.MEDIUM };

      case SnakesLaddersErrorType.CORRUPTED_DATA:
      case SnakesLaddersErrorType.SAVE_ERROR:
      case SnakesLaddersErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  // Convenience methods
  handleGameLogicError(reason: string): SnakesLaddersError {
    return this.handleError(
      SnakesLaddersErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleNotYourTurn(): SnakesLaddersError {
    return this.handleError(
      SnakesLaddersErrorType.NOT_YOUR_TURN,
      "It's not your turn. Please wait.",
      undefined
    );
  }

  handleConnectionError(error: any): SnakesLaddersError {
    return this.handleError(
      SnakesLaddersErrorType.CONNECTION_ERROR,
      'Connection lost. Attempting to reconnect...',
      error,
      true
    );
  }

  handleCorruptedData(dataType: string, error: any): SnakesLaddersError {
    return this.handleError(
      SnakesLaddersErrorType.CORRUPTED_DATA,
      'Unable to load saved game. Starting new game.',
      { dataType, error },
      true
    );
  }

  handleSaveError(gameId: string, error: any): SnakesLaddersError {
    return this.handleError(
      SnakesLaddersErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: any): SnakesLaddersError {
    return this.handleError(
      SnakesLaddersErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  // Internal helpers
  private isRecoverable(type: SnakesLaddersErrorType): boolean {
    switch (type) {
      case SnakesLaddersErrorType.INVALID_MOVE:
      case SnakesLaddersErrorType.NOT_YOUR_TURN:
      case SnakesLaddersErrorType.GAME_ENDED:
      case SnakesLaddersErrorType.GAME_LOGIC:
      case SnakesLaddersErrorType.CONNECTION_ERROR:
      case SnakesLaddersErrorType.CORRUPTED_DATA:
      case SnakesLaddersErrorType.AI_TIMEOUT:
      case SnakesLaddersErrorType.SAVE_ERROR:
      case SnakesLaddersErrorType.LOAD_ERROR:
        return true;
      default:
        return false;
    }
  }

  private logError(error: SnakesLaddersError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: SnakesLaddersError): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: 'Snakes & Ladders Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): SnakesLaddersError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
